import { c as _c } from "react/compiler-runtime";
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useContext, useEffect } from 'react';
import { useStore } from 'zustand';
import { useNavigationOverride } from '@/idv2/features/app-shell';
import { NavigationHeader } from '@/idv2/features/app-shell/navigation-header';
import { consumeContinueOnMobile, isContinueOnMobile, markTransferVisited } from '@/idv2/features/idv-to-idv2';
import { type IDV2Store, StoreContext, setIdv2StoreData } from '@/idv2/features/idv-to-idv2/idv2-provider';
import Transfer from '@/idv2/features/transfer/transfer';
import type { TransferMode, TransferRequirements } from '@/idv2/features/transfer/types';
import getTransferMode from '@/idv2/features/transfer/utils/get-transfer-mode';
import hasAppclipSupport from '@/idv2/features/transfer/utils/has-appclip-support';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import checkIsInIframe from '@/idv2/utils/check-is-in-iframe';
import checkIsSocialMediaBrowser from '@/idv2/utils/check-is-social-media-browser';
import getDevice from '@/idv2/utils/get-device';
import { redirectToCheckRequirements } from '@/idv2/utils/loader-navigation';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'transfer-route'
});
export const Route = createFileRoute('/onboarding/$token/transfer')({
  beforeLoad: async ({
    context: {
      allRequirements,
      config,
      queryClient,
      idDocOutcome
    },
    params: {
      token
    }
  }) => {
    logInfo('Transfer beforeLoad started');
    const fromContinueOnMobile = isContinueOnMobile();

    // Consume the continueOnMobile flag on entry (it allowed re-entry to transfer).
    // Don't mark transfer as visited yet — only mark it when transfer actually completes,
    // so that going back and then forward will correctly return to transfer.
    consumeContinueOnMobile();
    const device = await getDevice();
    const isInIframe = checkIsInIframe();
    const isSocialMediaBrowser = checkIsSocialMediaBrowser(config);

    // Extract transfer-relevant requirements from allRequirements
    const missingRequirements: TransferRequirements = {
      liveness: allRequirements.find(r => r.kind === 'liveness'),
      documents: allRequirements.filter(r => r.kind === 'collect_document')
    };
    const appclipSupported = hasAppclipSupport(allRequirements, config.isAppClipEnabled);
    const transferMode = getTransferMode({
      device,
      isSocialMediaBrowser,
      isInIframe,
      isTransferFromDesktopToMobileDisabled: config.flags?.idvTransferFromDesktopDisabled,
      missingRequirements,
      hasAppclipSupport: appclipSupported
    });
    logInfo('Transfer requirements determined', {
      hasLiveness: !!missingRequirements.liveness,
      documentCount: missingRequirements.documents.length,
      deviceType: device.type,
      isInIframe,
      isSocialMediaBrowser,
      transferMode
    });

    // If transfer mode is 'skip', mark visited to prevent infinite redirect loops
    // (shouldRunTransfer → redirect to transfer → skip → redirect back → shouldRunTransfer → ...)
    if (transferMode === 'skip') {
      logInfo('Transfer mode is skip, marking visited and redirecting to layout route');
      markTransferVisited();
      return redirectToCheckRequirements(queryClient, token);
    }
    return {
      device,
      idDocOutcome,
      isInIframe,
      missingRequirements,
      missingHandoffRequirements: allRequirements,
      transferMode,
      fromContinueOnMobile
    };
  },
  loader: ({
    context: {
      device,
      idDocOutcome,
      isInIframe,
      missingRequirements,
      missingHandoffRequirements,
      transferMode,
      fromContinueOnMobile
    }
  }) => {
    logInfo('Transfer loader started');
    return {
      device,
      idDocOutcome,
      isInIframe,
      missingRequirements,
      missingHandoffRequirements,
      transferMode,
      fromContinueOnMobile
    };
  },
  component: TransferPage
});
function TransferPage() {
  const $ = _c(23);
  const {
    device,
    idDocOutcome,
    isInIframe,
    missingRequirements,
    missingHandoffRequirements,
    transferMode,
    fromContinueOnMobile
  } = Route.useLoaderData();
  const {
    token
  } = Route.useParams();
  const {
    l10n,
    config
  } = Route.useRouteContext();
  const footprint = useFootprint();
  const {
    setNavigationOverridden
  } = useNavigationOverride();
  const store = useContext(StoreContext);
  const hideNavigationButtons = useStore(store, _temp);
  let t0;
  let t1;
  if ($[0] !== setNavigationOverridden) {
    t0 = () => {
      setNavigationOverridden(true);
      setIdv2StoreData({
        canGoBack: false
      });
      return () => setNavigationOverridden(false);
    };
    t1 = [setNavigationOverridden];
    $[0] = setNavigationOverridden;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== footprint.navigation) {
    t2 = () => {
      logInfo("Transfer completed");
      markTransferVisited();
      footprint.navigation.forward();
    };
    $[3] = footprint.navigation;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleDone = t2;
  let t3;
  if ($[5] !== footprint.navigation.close || $[6] !== hideNavigationButtons) {
    t3 = !hideNavigationButtons && <NavigationHeader canGoBack={false} onBack={_temp2} onClose={footprint.navigation.close} />;
    $[5] = footprint.navigation.close;
    $[6] = hideNavigationButtons;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = transferMode as Exclude<TransferMode, 'skip'>;
  let t5;
  if ($[8] !== config || $[9] !== device || $[10] !== fromContinueOnMobile || $[11] !== handleDone || $[12] !== idDocOutcome || $[13] !== isInIframe || $[14] !== l10n || $[15] !== missingHandoffRequirements || $[16] !== missingRequirements || $[17] !== t4 || $[18] !== token) {
    t5 = <Transfer authToken={token} device={device} config={config} isInIframe={isInIframe} l10n={l10n} missingRequirements={missingRequirements} missingHandoffRequirements={missingHandoffRequirements} sandboxIdDocOutcome={idDocOutcome} transferMode={t4} onDone={handleDone} fromContinueOnMobile={fromContinueOnMobile} />;
    $[8] = config;
    $[9] = device;
    $[10] = fromContinueOnMobile;
    $[11] = handleDone;
    $[12] = idDocOutcome;
    $[13] = isInIframe;
    $[14] = l10n;
    $[15] = missingHandoffRequirements;
    $[16] = missingRequirements;
    $[17] = t4;
    $[18] = token;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t5) {
    t6 = <>{t3}{t5}</>;
    $[20] = t3;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
}
function _temp2() {}
function _temp(state) {
  return state.hideNavigationButtons;
}